import React, { useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'

const IS_SERVER_SIDE = typeof window === 'undefined'

/**
 * @typedef {'top' | 'top-right' | 'top-left' | 'right' | 'bottom' | 'bottom-right' | 'bottom-left' | 'left'} Position
 */

/**
 * @typedef {Object} Offset
 *
 * @property {number} x - The x offset
 * @property {number} y - The y offset
 */

/**
 * @typedef {Object} CurvedArrowProps
 *
 * @property {string} [fromSelector='body'] - Selector for the starting element
 * @property {string} [toSelector] - Selector for the ending element
 * @property {Position} [fromPosition='right'] - Starting position
 * @property {Position} [toPosition='left'] - Ending position
 * @property {Offset} [fromOffset={ x: 0, y: 0 }] - Offset from start position
 * @property {Offset} [toOffset={ x: 0, y: 0 }] - Offset from end position
 * @property {number} [middleX=0] - X-axis control point adjustment
 * @property {number} [middleY=0] - Y-axis control point adjustment
 * @property {number} [width=8] - Line width
 * @property {string} [color='black'] - Arrow color
 * @property {number} [hideUnderWidth=0] - Hide arrow under specified screen width
 * @property {string} [hideIfFoundSelector] - Hide if selector matches an element
 * @property {boolean} [debugLine=false] - Show debug points
 * @property {number} [zIndex=0] - CSS z-index value
 */

/**
 * Calculate bezier curve point
 * @param {number} p0 - Start point
 * @param {number} p1 - Control point
 * @param {number} p2 - End point
 * @param {number} t - Time value (0-1)
 *
 * @returns {number}
 */
const bezier = (p0, p1, p2, t) => {
  return p1 + (1 - t) * (1 - t) * (p0 - p1) + t * t * (p2 - p1)
}

/**
 * Calculate min and max points of quadratic curve
 * @param {number} p0 - Start point
 * @param {number} p1 - Control point
 * @param {number} p2 - End point
 *
 * @returns {[number, number]} Min and max values
 */
const quadraticCurveMinMax = (p0, p1, p2) => {
  let min = Math.min(p0, p2)
  let max = Math.max(p0, p2)

  if (p0 - 2 * p1 + p2 !== 0) {
    const t = (p0 - p1) / (p0 - 2 * p1 + p2)

    if (t > 0 && t < 1) {
      const pMiddle = bezier(p0, p1, p2, t)
      min = Math.min(min, pMiddle)
      max = Math.max(max, pMiddle)
    }
  }

  return [Math.round(min), Math.round(max)]
}

/**
 * Calculate position offsets based on element position and dimensions
 * @param {Position} position - Position identifier
 * @param {DOMRect} rect - Element bounding rectangle
 * @param {Offset} [offset={ x: 0, y: 0 }] - Additional offset
 *
 * @returns {{ x: number, y: number }}
 */
const getPositionOffsets = (position, rect, offset = { x: 0, y: 0 }) => {
  const halfWidth = rect.width / 2
  const halfHeight = rect.height / 2

  let baseOffset

  switch (position) {
    case 'top':
      baseOffset = { x: 0, y: -halfHeight }
      break
    case 'top-right':
      baseOffset = { x: halfWidth, y: -halfHeight }
      break
    case 'top-left':
      baseOffset = { x: -halfWidth, y: -halfHeight }
      break
    case 'right':
      baseOffset = { x: halfWidth, y: 0 }
      break
    case 'bottom':
      baseOffset = { x: 0, y: halfHeight }
      break
    case 'bottom-right':
      baseOffset = { x: halfWidth, y: halfHeight }
      break
    case 'bottom-left':
      baseOffset = { x: -halfWidth, y: halfHeight }
      break
    case 'left':
      baseOffset = { x: -halfWidth, y: 0 }
      break
  }

  return {
    x: baseOffset.x + offset.x,
    y: baseOffset.y + offset.y
  }
}

/**
 * CurvedArrow Component
 * @param {CurvedArrowProps} props
 *
 * @returns {React.ReactElement | null}
 */
export const CurvedArrow = ({
  fromSelector = 'body',
  toSelector = fromSelector,
  fromPosition = 'right',
  toPosition = 'left',
  fromOffset = { x: 0, y: 0 },
  toOffset = { x: 0, y: 0 },
  middleX = 0,
  middleY = 0,
  width = 8,
  color = 'black',
  hideUnderWidth = 0,
  hideIfFoundSelector,
  debugLine = false,
  zIndex = 0
}) => {
  const canvasRef = useRef(null)
  const observerRef = useRef(null)

  const drawArrow = useCallback(() => {
    const canvas = canvasRef.current

    if (!canvas) return

    const fromElement = document.querySelector(fromSelector)
    const toElement = document.querySelector(toSelector)

    if (!fromElement || !toElement) return
    if (hideIfFoundSelector && document.querySelector(hideIfFoundSelector)) {
      const ctx = canvas.getContext('2d')

      if (!ctx) return

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      return
    }

    const fromRect = fromElement.getBoundingClientRect()
    const toRect = toElement.getBoundingClientRect()

    const fromOffsets = getPositionOffsets(fromPosition, fromRect, fromOffset)
    const toOffsets = getPositionOffsets(toPosition, toRect, toOffset)

    const p0x = fromRect.left + fromRect.width / 2 + fromOffsets.x
    const p0y = fromRect.top + fromRect.height / 2 + fromOffsets.y + window.scrollY

    const p2x = toRect.left + toRect.width / 2 + toOffsets.x
    const p2y = toRect.top + toRect.height / 2 + toOffsets.y + window.scrollY

    const p1x = (p0x + p2x) / 2 + middleX
    const p1y = (p0y + p2y) / 2 - middleY

    const settings = {
      p0x,
      p0y,
      p1x,
      p1y,
      p2x,
      p2y,
      size: 30,
      lineWidth: width,
      strokeStyle: color
    }

    const xMinMax = quadraticCurveMinMax(settings.p0x, settings.p1x, settings.p2x)
    const yMinMax = quadraticCurveMinMax(settings.p0y, settings.p1y, settings.p2y)

    const padding = settings.size - settings.lineWidth

    const xMin = xMinMax[0] - padding
    const xMax = xMinMax[1] + padding
    const yMin = yMinMax[0] - padding
    const yMax = yMinMax[1] + padding

    const p0xCanvas = settings.p0x - xMin
    const p0yCanvas = settings.p0y - yMin
    const p1xCanvas = settings.p1x - xMin
    const p1yCanvas = settings.p1y - yMin
    const p2xCanvas = settings.p2x - xMin
    const p2yCanvas = settings.p2y - yMin

    canvas.style.position = 'absolute'
    canvas.style.pointerEvents = 'none'
    canvas.style.top = yMin + 'px'
    canvas.style.left = xMin + 'px'
    canvas.width = xMax - xMin
    canvas.height = yMax - yMin

    if (hideUnderWidth > 0) {
      canvas.style.display = window.matchMedia(`(max-width: ${hideUnderWidth}px)`).matches ?
        'none' :
        'inline-block'
    }

    if (zIndex) {
      canvas.style.zIndex = String(zIndex)
    }

    const ctx = canvas.getContext('2d')

    if (!ctx) return

    if (debugLine) {
      ctx.arc(p0xCanvas, p0yCanvas, 10, 0, 2 * Math.PI)
      ctx.stroke()
      ctx.arc(p1xCanvas, p1yCanvas, 10, 0, 2 * Math.PI)
      ctx.stroke()
      ctx.arc(p2xCanvas, p2yCanvas, 10, 0, 2 * Math.PI)
      ctx.stroke()
    }

    // Styling
    ctx.strokeStyle = settings.strokeStyle
    ctx.lineWidth = settings.lineWidth
    ctx.lineJoin = 'round'
    ctx.lineCap = 'round'

    // Arrow body
    ctx.beginPath()
    ctx.moveTo(p0xCanvas, p0yCanvas)
    ctx.quadraticCurveTo(p1xCanvas, p1yCanvas, p2xCanvas, p2yCanvas)
    ctx.stroke()

    // Arrow head
    const angle = Math.atan2(p2yCanvas - p1yCanvas, p2xCanvas - p1xCanvas)
    ctx.translate(p2xCanvas, p2yCanvas)

    // Right side
    ctx.rotate(angle + 1)
    ctx.beginPath()
    ctx.moveTo(0, settings.size)
    ctx.lineTo(0, 0)
    ctx.stroke()

    // Left side
    ctx.rotate(-2)
    ctx.lineTo(0, -settings.size)
    ctx.stroke()

    // Restore context
    ctx.rotate(1 - angle)
    ctx.translate(-p2xCanvas, -p2yCanvas)
  }, [
    fromSelector,
    toSelector,
    fromPosition,
    toPosition,
    fromOffset,
    toOffset,
    middleX,
    middleY,
    width,
    color,
    hideUnderWidth,
    hideIfFoundSelector,
    debugLine,
    zIndex
  ])

  useEffect(() => {
    if (IS_SERVER_SIDE) return

    const fromElement = document.querySelector(fromSelector)
    const toElement = document.querySelector(toSelector)

    if (!fromElement || !toElement) return

    // Initial draw
    drawArrow()

    // Setup resize observer for both elements
    observerRef.current = new ResizeObserver(drawArrow)

    observerRef.current.observe(fromElement)

    if (fromElement !== toElement) {
      observerRef.current.observe(toElement)
    }

    // Setup window resize listener
    window.addEventListener('resize', drawArrow)

    return () => {
      observerRef.current?.disconnect()
      window.removeEventListener('resize', drawArrow)
    }
  }, [drawArrow, fromSelector, toSelector])

  if (IS_SERVER_SIDE) return null

  return <canvas ref={canvasRef} />
}

CurvedArrow.propTypes = {
  fromSelector: PropTypes.string,
  toSelector: PropTypes.string,
  fromPosition: PropTypes.oneOf(['top', 'top-right', 'top-left', 'right', 'bottom', 'bottom-right', 'bottom-left', 'left']),
  toPosition: PropTypes.oneOf(['top', 'top-right', 'top-left', 'right', 'bottom', 'bottom-right', 'bottom-left', 'left']),
  fromOffset: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  }),
  toOffset: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  }),
  middleX: PropTypes.number,
  middleY: PropTypes.number,
  width: PropTypes.number,
  color: PropTypes.string,
  hideUnderWidth: PropTypes.number,
  hideIfFoundSelector: PropTypes.string,
  debugLine: PropTypes.bool,
  zIndex: PropTypes.number
}
